import apiClient from "./apiClient"

/**
 * Files attached in the agent chat. One mechanism, two behaviours: a file is
 * STAGED on attach (this service's `stageFile`), and FILING it into a module is a
 * separate, later step (`routeFile`) the user confirms — see
 * `docs/superpowers/specs/2026-08-19-chat-file-upload-design.md`.
 *
 * Replaces the old `heldFile` mechanism (a `File` object kept in React state,
 * described to the model as `{name, contentType, size}` and never actually
 * uploaded until a destination-specific tool ran). The file now lives server-side
 * from the moment it is attached, so it survives a refresh and the model can
 * actually read it.
 */

export type ConversationFileStatus = "staged" | "routed"

export interface ConversationFile {
  id: string
  conversationId: string
  fileName: string
  contentType: string | null
  sizeBytes: number
  status: ConversationFileStatus
  routedTo: string | null
  createdAt: Date
}

// apiClient's response interceptor already camelCases every JSON body (and
// parses `createdAt`/`updatedAt` into Date instances), so these response
// shapes are the camelCase wire shape as apiClient callers see it — NOT a
// second manual mapping from snake_case. Re-deriving field names here (as a
// separate `...ApiResponse` interface + `fromApi`) previously read fields
// that no longer existed on the parsed object (e.g. `response.file_name`
// after the interceptor had already renamed it to `fileName`), so every
// field silently came back `undefined`.

export interface RouteChatFileResult {
  destination: string
  ref: string
  finalFileName: string
  renamed: boolean
  message: string
}

export const chatFilesApiCalls = {
  stageFile: async (
    conversationId: string,
    file: File,
  ): Promise<ConversationFile> => {
    const formData = new FormData()
    formData.append("file", file)
    const response = await apiClient.post<ConversationFile>(
      `/chat/conversations/${conversationId}/files`,
      formData,
    )
    return response.data
  },

  listFiles: async (conversationId: string): Promise<ConversationFile[]> => {
    const response = await apiClient.get<ConversationFile[]>(
      `/chat/conversations/${conversationId}/files`,
    )
    return response.data
  },

  removeFile: async (
    conversationId: string,
    fileId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/chat/conversations/${conversationId}/files/${fileId}`,
    )
  },

  routeFile: async (
    conversationId: string,
    fileId: string,
    destination: string,
  ): Promise<RouteChatFileResult> => {
    const response = await apiClient.post<RouteChatFileResult>(
      `/chat/conversations/${conversationId}/files/${fileId}/route`,
      { destination },
    )
    return response.data
  },
}
